
import React from 'react';
import { TRANSLATIONS } from '../../constants';
import { Language } from '../../types';

interface AuthVisualsProps {
  isMobileFormVisible: boolean;
  setIsMobileFormVisible: (visible: boolean) => void;
  language: Language;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  onSwitchLanguage: (lang: Language) => void;
  featuresSlides: any[];
  currentSlide: number;
  setCurrentSlide: (index: number) => void;
  renderMockup: (type: string) => React.ReactNode;
}

const AuthVisuals: React.FC<AuthVisualsProps> = ({
  isMobileFormVisible, setIsMobileFormVisible, language, theme,
  onToggleTheme, onSwitchLanguage, featuresSlides, currentSlide,
  setCurrentSlide, renderMockup
}) => {
  const t = TRANSLATIONS[language];
  const isRtl = language === 'ar';

  return (
    <div className={`w-full lg:w-1/2 relative overflow-hidden bg-[#050507] flex-col ${isMobileFormVisible ? 'hidden lg:flex' : 'flex'}`}>
       {/* Backgrounds */}
       <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/10 via-[#050507] to-[#050507] z-0 pointer-events-none"></div>
       <div className="absolute top-[-20%] left-[-20%] w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-[120px] pointer-events-none"></div>

       {/* Header */}
       <div className="absolute top-0 left-0 right-0 z-50 p-6 flex justify-between items-center pointer-events-none">
          <div className="pointer-events-auto lg:hidden">
             <button onClick={() => setIsMobileFormVisible(true)} className="group flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/10 px-4 py-2.5 rounded-xl transition-all shadow-lg active:scale-95">
                <span className="text-white text-[10px] font-black uppercase tracking-wider">{isRtl ? 'دخول / تسجيل' : 'Login / Join'}</span>
                <i className={`fas fa-arrow-${isRtl ? 'left' : 'right'} text-white text-[10px] group-hover:translate-x-${isRtl ? '-1' : '1'} transition-transform`}></i>
             </button>
          </div>
          <div className="hidden lg:block"></div> 
          <div className="pointer-events-auto flex gap-2">
             <button onClick={() => onSwitchLanguage(language === 'ar' ? 'en' : 'ar')} className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 backdrop-blur-xl border border-white/10 flex items-center justify-center text-white transition-all active:scale-95">
                <span className="text-[10px] font-black">{language === 'ar' ? 'EN' : 'ع'}</span>
             </button>
             <button onClick={onToggleTheme} className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 backdrop-blur-xl border border-white/10 flex items-center justify-center text-white transition-all active:scale-95">
                <i className={`fas ${theme === 'dark' ? 'fa-sun' : 'fa-moon'} text-xs`}></i>
             </button>
          </div>
       </div>

       {/* Hero Content */}
       <div className="relative z-20 px-8 lg:px-12 pt-28 shrink-0">
           <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-emerald-500 rounded-2xl flex items-center justify-center text-2xl shadow-lg shadow-emerald-500/20">
                  <img src="logo.png" className="w-8 h-8 object-contain brightness-0 invert" alt="L" />
              </div>
              <h2 className="text-xl font-black text-white tracking-widest uppercase">{t.appName}</h2>
           </div>
           
           <h1 className="text-4xl lg:text-5xl font-black text-white mb-4 leading-tight">
              {isRtl ? 'الذكاء الاصطناعي' : 'Artificial Intelligence'} <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-600">
                 {isRtl ? 'بروح سعودية.' : 'with a Saudi Soul.'}
              </span>
           </h1>
           <p className="text-slate-400 text-base max-w-md leading-relaxed font-medium">
              {isRtl 
                ? 'انضم إلى مجتمع لهجة واستمتع بتجربة ذكاء اصطناعي تفهم ثقافتك وتتحدث بلسانك. منصة متكاملة للشركات والأفراد.' 
                : 'Join the Lahja community and enjoy an AI experience that understands your culture and speaks your language. A complete platform for businesses and individuals.'}
           </p>
       </div>

       {/* Carousel */}
       <div className="flex-1 relative w-full flex flex-col justify-center items-center z-10 px-12">
          <div className="relative w-full h-[320px]">
             {featuresSlides.map((slide, index) => (
                <div key={slide.id} className={`absolute inset-0 flex flex-col justify-center transition-all duration-700 ease-in-out transform ${index === currentSlide ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95 pointer-events-none'}`}>
                   <div className="flex-1 flex items-center justify-center mb-6">
                      {renderMockup(slide.visual)}
                   </div>
                   <div className="text-center lg:text-start">
                      <h3 className="text-2xl font-black text-white mb-2">{isRtl ? slide.title.ar : slide.title.en}</h3>
                      <p className="text-slate-400 text-[15px] max-w-sm mx-auto lg:mx-0 leading-relaxed">
                         {isRtl ? slide.desc.ar : slide.desc.en}
                      </p>
                   </div>
                </div>
             ))}
          </div>
       </div>

       {/* Dots */}
       <div className="relative z-20 px-12 pb-12 lg:pb-12">
          <div className="flex gap-3 justify-center lg:justify-start">
             {featuresSlides.map((_, idx) => (
                <button key={idx} onClick={() => setCurrentSlide(idx)} className={`h-1 rounded-full transition-all duration-500 ${idx === currentSlide ? 'w-5 bg-emerald-500' : 'w-1.5 bg-slate-700 hover:bg-slate-600'}`}></button>
             ))}
          </div>
       </div>
    </div>
  );
};

export default AuthVisuals;
